import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './styles.scss';
import { Loading } from '@ui-elements';
import { StockItem } from './stockItem';

interface Props {
  postcode: string;
  model: string;
  matchCode: string;
  onClose: () => void;
  onSelectedModel: (stockItem: StockItem) => void;
}

const StockViewer: React.FC<Props> = ({
  postcode,
  model,
  matchCode,
  onClose,
  onSelectedModel,
}) => {
  const [showModal, setShowModal] = useState(true);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [showLoading, setShowLoading] = useState(true);

  const getSlick = useCallback(() => {
    const codes =
      (document.getElementById('modelCodes') as HTMLInputElement)?.value || '';
    const slickElements = document.getElementsByClassName('slick-initialized');

    if (!codes) return;

    const codeArray = codes.split(',');
    const matchedCode = codeArray.find((code) =>
      window.location.pathname.includes(`/${code}/`)
    );

    if (matchedCode) {
      if (slickElements.length > 0) {
        Array.from(document.querySelectorAll('.slick-initialized img')).forEach(
          (imgEl, index) => {
            const img = imgEl as HTMLImageElement;
            if (!img.classList.contains('out-of-stock-var')) {
              const btn = document.createElement('a');
              btn.id = 'configureViewStock';
              btn.className = 'fancy-link';
              btn.style.cssText =
                'text-align:center; display:none; padding:15px 0; font-size:20px; color:black;';
              btn.innerText = 'View Stock';
              btn.onclick = () => showModel();
              img
                .closest('.slick-initialized')
                ?.parentElement?.appendChild(btn);

              if (index === 0) btn.click();
            }
          }
        );
      } else {
        setTimeout(getSlick, 1000);
      }
    }
  }, []);

  const showModel = () => {
    bindTable();
  };

  const bindTable = async () => {
    const apiUrl = `/api/stocks/${model}/${postcode}?modelCode=${matchCode}`;

    try {
      const response = await axios.get(apiUrl);
      setStockItems(response.data.stockItems || []);
      setShowModal(true);
      setShowLoading(false);
      document.querySelectorAll<HTMLElement>('.tt').forEach((tt) => {
        tt.addEventListener('mouseenter', () => {
          const content = tt.querySelector<HTMLElement>('.tt__content');
          if (!content) return;

          content.style.visibility = 'hidden';
          content.style.display = 'block';
          const contentRect = content.getBoundingClientRect();
          const table = tt.closest('table');

          if (!table) return;

          const tableRect = table.getBoundingClientRect();
          const isOverflowingTop = contentRect.top - 20 < tableRect.top;
          const isOverflowingLeft = contentRect.left - 10 < tableRect.left;

          content.style.visibility = '';
          content.style.display = '';

          if (isOverflowingTop) {
            content.style.top = '125%'; // below icon
            content.style.bottom = 'auto';
            content.style.transform = 'translateX(-50%)';
          }

          if (isOverflowingLeft) {
            content.style.left = '7%'; // move more to the right
            content.style.transform = 'translateX(-30%)';
          }
        });
      });
    } catch (error) {
      console.error('Failed to fetch stock data', error);
      setShowLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    onClose();
  };

  const handleSelectModel = (stockItem: StockItem) => {
    onSelectedModel(stockItem);
    closeModal();
  };

  useEffect(() => {
    showModel();
    getSlick();

    /*
    const closeBtn = document.getElementById('popupClose');
    closeBtn?.addEventListener('click', closeModal);

    return () => {
      closeBtn?.removeEventListener('click', closeModal);
    };
    */
  }, [getSlick]);

  const renderStockItems = () => {
    return stockItems.length > 0 ? (
      <table className="table-scroll">
        <thead>
          <tr>
            <td>VARIANT</td>
            <td>COLOUR/TRIM</td>
            <td>Availability</td>
            <td />
          </tr>
        </thead>
        <tbody className="body-half-screen" id="stocksData">
          {stockItems.map((item, index) => (
            <tr key={index}>
              <td>{item.displayName}</td>
              <td>{item.exteriorColourDescription}</td>
              <td>
                {item.timeFrame.replace('Available Now: ', '')}
                {parseInt(item.stockCount, 10) <= 5 && (
                  <span className="i-alert"> ! </span>
                )}
                <span
                  className="tt"
                  style={{
                    height: '15px',
                    verticalAlign: 'top',
                    whiteSpace: 'normal',
                    wordBreak: 'normal',
                    overflowWrap: 'break-word',
                  }}
                >
                  <i className="icon icon--info" />
                  <span className="tt__content">
                    {item.customerInformation?.includes(
                      'Ready for Despatch'
                    ) ? (
                      <>
                        “Ready for Despatch” means this particular variant,
                        colour and trim combination is currently available in
                        our vehicle storage facility and ready for despatch to
                        your preferred Subaru Retailer.
                        <br />
                        <br />
                        Estimated delivery timeframes vary by location –
                        typically up to one week for metropolitan retailers, up
                        to two weeks for regional retailers, and two to four
                        weeks for retailers in the Northern Territory or
                        Tasmania.
                        <br />
                        <br />
                        For more detailed information, please contact your
                        preferred Subaru Retailer, who can provide a more
                        specific delivery timing and availability updates.
                      </>
                    ) : (
                      <>
                        The estimated vehicle availability timeframes provided
                        are intended as a guide only.
                        <br />
                        <br />
                        Actual delivery timing may vary depending on vehicle
                        allocation and retailer location – typically,
                        metropolitan retailers receive delivery up to two weeks
                        earlier than regional retailers, and two to four weeks
                        earlier than retailers in the Northern Territory or
                        Tasmania.
                      </>
                    )}
                  </span>
                </span>
              </td>
              <td>
                <a className="btn" onClick={() => handleSelectModel(item)}>
                  Select
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <>
        {' '}
        <div id="stock-loader">
          Sorry, there are no available stocks for the selected postcode at the
          moment.
        </div>
      </>
    );
  };

  return (
    <>
      <div id="view-stock-modal" className={'fancy-modal show'}>
        <div className="fancy-modal-wrapper">
          <div className="fancy-modal-title">
            <i
              data-close="true"
              className="popupClose popup__close fancy-close veiw-stock-close"
              role="button"
              onClick={closeModal}
            >
              <span>Close</span>
            </i>
            <h2 id="stockPopupTitle">Select your Subaru {model} model</h2>
            <div className="fancy-modal-content step-2">
              <div className="fancy-modal-body">
                {showLoading ? (
                  <div id="stock-loader">
                    <Loading />
                  </div>
                ) : (
                  renderStockItems()
                )}
                <p className="bottom-text">
                  <span className="i-alert">!</span> indicates low stock levels
                </p>
                <div className="pagination-container">
                  <nav className="data-pagination lg-none">
                    <ul className="stockPagination">
                      <li data-page="prev" className="prev-link">
                        <span>
                          <i className="fa fa-chevron-left" />
                        </span>
                      </li>
                      <li data-page="next" id="prev" className="next-link">
                        <span>
                          <i className="fa fa-chevron-right" />
                        </span>
                      </li>
                    </ul>
                  </nav>
                </div>
                <p className="bottom-text fw-300">
                  Stock availability timeframes are based on third party
                  information available to Subaru Australia as at the date of
                  publication. Whilst all reasonable efforts have been made to
                  ensure accuracy of the stock availability timeframes, they are
                  indicative only and subject to change. Your selected vehicle
                  preference is not secured until an order has been placed and a
                  signed contract has been accepted by Subaru Australia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockViewer;
